// apps/web/src/lib/snapshot.ts
// The store behind every "paint the last thing we saw, then revalidate"
// snapshot in the app.
//
// One prefix, so logging out clears every snapshot by walking the namespace
// rather than by a hand-kept list of keys — a list is exactly the thing that
// drifts, and the failure mode is one user's day appearing under another
// account.
//
// localStorage is the store today. A WKWebView can evict it under storage
// pressure, which turns "sometimes the app launches slowly" into a report
// nobody can reproduce; moving to Capacitor Preferences is a change to this
// file alone.

const PREFIX = "caloly.snap.";

interface Envelope<T> {
  at: number;
  value: T;
}

/** Read a snapshot, or null if absent, unreadable, or older than maxAgeMs. */
export function readSnapshot<T>(key: string, maxAgeMs?: number): T | null {
  try {
    const raw = localStorage.getItem(PREFIX + key);
    if (!raw) return null;
    const env = JSON.parse(raw) as Envelope<T>;
    if (typeof env?.at !== "number") return null;
    if (maxAgeMs !== undefined && Date.now() - env.at > maxAgeMs) return null;
    return env.value;
  } catch { return null; }
}

export function writeSnapshot<T>(key: string, value: T): void {
  try {
    localStorage.setItem(PREFIX + key, JSON.stringify({ at: Date.now(), value } satisfies Envelope<T>));
  } catch {
    // Quota exceeded or storage disabled. A missing snapshot only costs a
    // skeleton on the next launch, so there is nothing to recover from here.
  }
}

export function removeSnapshot(key: string): void {
  try { localStorage.removeItem(PREFIX + key); } catch { /* storage unavailable */ }
}

/** Drop every snapshot. Call on logout — see the note about the namespace. */
export function clearSnapshots(): void {
  try {
    const doomed: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k?.startsWith(PREFIX)) doomed.push(k);
    }
    for (const k of doomed) localStorage.removeItem(k);
  } catch { /* storage unavailable */ }
}
